import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Button,
  SearchableDropdown,
  LocationPicker,
  LocationValue,
  showSuccessToast,
  showErrorToast,
} from '@/components';
import {
  ImportCommitResult,
  ManualShipmentServiceType,
  MANUAL_SHIPMENT_SERVICE_TYPES,
  MANUAL_SHIPMENT_SIZES,
  manualShipmentSizeVolume,
  ShipmentImportError,
  buildManualShipmentCsvFile,
  importShipmentCommit,
} from '@/services/api/shipments';
import { getListPitstop, Pitstop } from '@/services/api/pitstops';
import { getClientServiceCapabilities } from '@/services/api/clientAuth';
import { useAuth } from '@/contexts/AuthContext';

interface CreateShipmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: number;
  onCreated: (result: ImportCommitResult) => void;
}

// Package types offered by the form. There's no dedicated type column in the
// import contract, so the choice is folded into the item description.
const PACKAGE_TYPES = [
  'Document',
  'Food',
  'Medical Items',
  'Electronics',
  'Fragile Items',
  'General goods',
  'Other',
] as const;

// Origin / fulfilment modes. Only "Dash Hub" is enabled for now; the other two
// are placeholders matching the prototype + ImportModal.
const ORIGIN_MODES: { value: string; label: string; disabled: boolean }[] = [
  { value: 'dash', label: 'Dash Hub', disabled: false },
  { value: 'client', label: 'Hub Saya · Soon', disabled: true },
  { value: 'direct', label: 'Direct · Soon', disabled: true },
];

// Remix icon per service tier (visual only — matches the prototype's cards).
const SERVICE_ICONS: Record<string, string> = {
  INSTANT: 'ri-flashlight-fill',
  SAME_DAY: 'ri-sun-line',
  NEXT_DAY: 'ri-calendar-line',
  REGULAR: 'ri-truck-line',
};

interface PackageDraft {
  name: string;
  weightKg: string;
  size: string;
  type: string;
  description: string;
}

const emptyPackage = (): PackageDraft => ({
  name: '',
  weightKg: '',
  size: 'M',
  type: 'General goods',
  description: '',
});

const emptyDestination = (): LocationValue => ({
  address: '',
  lat: null,
  long: null,
});

const friendlyError = (err: unknown): string => {
  if (err instanceof ShipmentImportError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Terjadi kesalahan tak terduga.';
};

// ── Small presentational helpers ───────────────────────────────────────────
// Subtle numbered section eyebrow (matches the prototype's "1 · LOKASI ASAL").
const SectionHeading: React.FC<{
  index: number;
  title: string;
  right?: React.ReactNode;
}> = ({ index, title, right }) => (
  <div className="mb-2 mt-3 flex items-center justify-between gap-3 border-t border-stroke-sub-300 pt-7 first:mt-0 first:border-t-0 first:pt-0">
    <div className="text-sm font-bold uppercase tracking-[0.14em] text-text-sub-600">
      <span className="text-primary">{index}</span>
      <span className="mx-1.5 text-text-soft-400">·</span>
      {title}
    </div>
    {right}
  </div>
);

const Field: React.FC<{
  label: string;
  required?: boolean;
  optional?: boolean;
  children: React.ReactNode;
}> = ({ label, required, optional, children }) => (
  <div>
    <label className="mb-1.5 block text-[13px] font-semibold text-text-strong-950">
      {label}
      {required && <span className="ml-0.5 text-error-base">*</span>}
      {optional && (
        <span className="ml-1 font-normal text-text-soft-400">· opsional</span>
      )}
    </label>
    {children}
  </div>
);

const inputCls =
  'w-full rounded-lg border border-stroke-sub-300 bg-bg-white-0 px-3.5 py-2.5 text-sm text-text-strong-950 outline-none transition-colors placeholder:text-text-soft-400 focus:border-primary';

const CreateShipmentModal: React.FC<CreateShipmentModalProps> = ({
  isOpen,
  onClose,
  clientId,
  onCreated,
}) => {
  const { user } = useAuth();

  // Origin (Dash Hub).
  const [hubs, setHubs] = useState<Pitstop[]>([]);
  const [hubsLoading, setHubsLoading] = useState(false);
  const [originMode, setOriginMode] = useState('dash');
  const [hubId, setHubId] = useState('');
  const [senderName, setSenderName] = useState('');
  const [originPhone, setOriginPhone] = useState('');

  // Destination.
  const [receiverName, setReceiverName] = useState('');
  const [destination, setDestination] =
    useState<LocationValue>(emptyDestination);
  const [destinationPhone, setDestinationPhone] = useState('');

  // Packages + options.
  const [packages, setPackages] = useState<PackageDraft[]>([emptyPackage()]);
  const [serviceType, setServiceType] =
    useState<ManualShipmentServiceType>('INSTANT');
  const [bookingId, setBookingId] = useState('');

  // Client capabilities from /clients/me/price-bounds: which service tiers the
  // client may use, and the max per-shipment weight. Null tiers = not loaded
  // yet / lookup failed → fall back to showing every tier.
  const [allowedServiceTypes, setAllowedServiceTypes] = useState<
    string[] | null
  >(null);
  const [maxWeightKg, setMaxWeightKg] = useState<number | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);

  // Reset everything each time the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    setOriginMode('dash');
    setHubId('');
    setSenderName('');
    setOriginPhone('');
    setReceiverName('');
    setDestination(emptyDestination());
    setDestinationPhone('');
    setPackages([emptyPackage()]);
    setServiceType('INSTANT');
    setBookingId('');
    setAllowedServiceTypes(null);
    setMaxWeightKg(null);
    setSubmitting(false);
    setInlineError(null);
  }, [isOpen]);

  // Clear a shown error the moment the user edits any field, so a resolved
  // problem doesn't keep showing its message until the next submit. Keyed on
  // the fields only (not inlineError), so setting an error still persists it.
  useEffect(() => {
    setInlineError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hubId,
    receiverName,
    destination,
    destinationPhone,
    senderName,
    originPhone,
    packages,
    serviceType,
    bookingId,
  ]);

  // Lock the body scroll behind the full-screen overlay.
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  // Load the staffed Dash Hubs once open.
  useEffect(() => {
    if (!isOpen) return;
    let active = true;
    setHubsLoading(true);
    getListPitstop({ type: 'HUB', hasStaff: true, page: '1', size: '100' })
      .then((res) => {
        if (active) setHubs(res.data ?? []);
      })
      .catch(() => {
        if (active) setHubs([]);
      })
      .finally(() => {
        if (active) setHubsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [isOpen]);

  // Load the client's entitled service tiers + weight cap (price-bounds). Never
  // throws; on failure allowedServiceTypes stays null → every tier is shown.
  useEffect(() => {
    if (!isOpen) return;
    let active = true;
    getClientServiceCapabilities().then((caps) => {
      if (!active) return;
      setMaxWeightKg(caps.maxWeightKg);
      setAllowedServiceTypes(caps.serviceTypes.length ? caps.serviceTypes : null);
    });
    return () => {
      active = false;
    };
  }, [isOpen]);

  // The tiers actually shown: the client's entitled set (order preserved from
  // our canonical list), or all tiers when price-bounds hasn't scoped them.
  const visibleServiceTypes = useMemo(
    () =>
      allowedServiceTypes
        ? MANUAL_SHIPMENT_SERVICE_TYPES.filter((s) =>
            allowedServiceTypes.includes(s.value)
          )
        : [...MANUAL_SHIPMENT_SERVICE_TYPES],
    [allowedServiceTypes]
  );

  // Keep the selection valid: if the chosen tier isn't in the entitled set,
  // snap to the first available one.
  useEffect(() => {
    if (!visibleServiceTypes.length) return;
    if (!visibleServiceTypes.some((s) => s.value === serviceType)) {
      setServiceType(visibleServiceTypes[0].value);
    }
  }, [visibleServiceTypes, serviceType]);

  // Prefill the sender name from the chosen hub (origin is backfilled from the
  // hub at commit; the sender name is editable).
  const handleHubChange = (value: string) => {
    setHubId(value);
    const hub = hubs.find((h) => String(h.id) === String(value));
    if (hub) setSenderName((prev) => prev || hub.name);
  };

  const updatePackage = (index: number, patch: Partial<PackageDraft>) =>
    setPackages((prev) =>
      prev.map((pkg, i) => (i === index ? { ...pkg, ...patch } : pkg))
    );
  const addPackage = () => setPackages((prev) => [...prev, emptyPackage()]);
  const removePackage = (index: number) =>
    setPackages((prev) =>
      prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)
    );

  const namedPackages = useMemo(
    () => packages.filter((p) => p.name.trim() !== ''),
    [packages]
  );
  const totalWeight = useMemo(
    () =>
      namedPackages.reduce((sum, p) => sum + (parseFloat(p.weightKg) || 0), 0),
    [namedPackages]
  );
  const totalVolume = useMemo(
    () =>
      namedPackages.reduce(
        (sum, p) => sum + manualShipmentSizeVolume(p.size),
        0
      ),
    [namedPackages]
  );

  // Client-side gate before hitting the network. Returns a message or null.
  const validate = (): string | null => {
    if (!hubId) return 'Pilih Dash Hub asal terlebih dahulu.';
    if (!receiverName.trim()) return 'Nama penerima wajib diisi.';
    if (destination.lat == null || destination.long == null)
      return 'Tentukan lokasi tujuan (cari alamat atau taruh pin di peta).';
    if (namedPackages.length === 0)
      return 'Tambahkan minimal satu paket dengan nama.';
    const names = namedPackages.map((p) => p.name.trim());
    if (new Set(names).size !== names.length)
      return 'Nama paket harus unik — ada nama yang terduplikat.';
    if (maxWeightKg != null && totalWeight > maxWeightKg)
      return `Total berat (${totalWeight.toFixed(2)} kg) melebihi batas maksimum ${maxWeightKg} kg untuk akun ini.`;
    return null;
  };

  const handleSubmit = async () => {
    const problem = validate();
    if (problem) {
      setInlineError(problem);
      return;
    }
    setInlineError(null);
    setSubmitting(true);
    try {
      const file = buildManualShipmentCsvFile({
        bookingId: bookingId.trim() || undefined,
        receiverName: receiverName.trim(),
        senderName: senderName.trim() || undefined,
        originPhone: originPhone.trim() || undefined,
        destinationAddress: destination.address.trim(),
        destinationLat: destination.lat as number,
        destinationLong: destination.long as number,
        destinationPhone: destinationPhone.trim() || undefined,
        serviceType,
        packages: namedPackages.map((p) => ({
          name: p.name.trim(),
          weightKg: p.weightKg.trim() || undefined,
          size: p.size,
          type: p.type,
          description: p.description.trim() || undefined,
        })),
      });

      const result = await importShipmentCommit({
        clientId,
        file,
        hubId: Number(hubId),
        uploadedBy:
          user?.email || user?.displayName || user?.username || undefined,
      });

      onCreated(result);
      showSuccessToast(
        'Shipment dibuat',
        `${result.createdShipments} shipment · ${namedPackages.length} paket berhasil dibuat.`
      );
      onClose();
    } catch (err) {
      // The commit validates the synthesised CSV; surface any row-level
      // validation messages inline (mapped to friendly copy by the API layer).
      let message = friendlyError(err);
      if (err instanceof ShipmentImportError && err.preview?.errors?.length) {
        message = err.preview.errors
          .slice(0, 4)
          .map((e) => `${e.field}: ${e.message}`)
          .join(' · ');
      }
      setInlineError(message);
      showErrorToast('Gagal membuat shipment', message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const selectedHub = hubs.find((h) => String(h.id) === String(hubId));

  return createPortal(
    <div className="fixed inset-0 z-50 flex flex-col bg-bg-weak-50">
      {/* Header */}
      <header className="flex shrink-0 items-center gap-3 border-b border-stroke-sub-300 bg-bg-white-0 px-4 py-3 lg:px-6">
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          aria-label="Tutup"
          className="flex h-9 w-9 items-center justify-center rounded-lg text-text-soft-400 transition-colors hover:bg-bg-weak-50 hover:text-text-strong-950 disabled:opacity-50"
        >
          <i className="ri-close-line text-xl" />
        </button>
        <div>
          <h2 className="text-base font-bold tracking-tight text-text-strong-950">
            Buat Shipment
          </h2>
          <p className="text-xs text-text-sub-600">
            Buat satu shipment manual. Untuk banyak sekaligus, gunakan Import
            CSV.
          </p>
        </div>
      </header>

      {/* Body — cards on a gray canvas, centered as a group. */}
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto flex max-w-[1080px] flex-col items-start gap-6 px-4 py-6 lg:flex-row lg:px-6 lg:py-8">
          {/* LEFT: main form card */}
          <div className="w-full flex-1 rounded-2xl border border-stroke-sub-300 bg-bg-white-0 p-5 shadow-card-xs lg:p-7">
            <h2 className="text-lg font-bold tracking-tight text-text-strong-950">
              Detail Pengiriman
            </h2>
            <p className="mt-1 text-[13px] text-text-sub-600">
              Isi detail pengiriman di bawah. Untuk banyak pengiriman sekaligus,
              gunakan Import CSV.
            </p>

            {/* 1 · Origin */}
            <SectionHeading index={1} title="Lokasi Asal" />
            <div className="space-y-5">
              <Field label="Asal pengiriman">
                <div className="grid grid-cols-1 gap-1 rounded-lg border border-stroke-sub-300 bg-bg-weak-50 p-1 sm:grid-cols-3">
                  {ORIGIN_MODES.map((m) => {
                    const active = originMode === m.value;
                    return (
                      <button
                        key={m.value}
                        type="button"
                        disabled={m.disabled}
                        onClick={() => !m.disabled && setOriginMode(m.value)}
                        title={m.disabled ? 'Segera hadir' : undefined}
                        className={`rounded-md px-3 py-1.5 text-[13px] font-semibold transition-colors ${
                          active
                            ? 'bg-bg-white-0 text-primary shadow-card-xs'
                            : m.disabled
                              ? 'cursor-not-allowed text-text-soft-400'
                              : 'text-text-sub-600 hover:text-text-strong-950'
                        }`}
                      >
                        {m.label}
                      </button>
                    );
                  })}
                </div>
              </Field>

              <Field label="Pilih Dash Hub asal" required>
                <SearchableDropdown
                  options={hubs.map((h) => ({ value: h.id, label: h.name }))}
                  value={hubId}
                  onChange={handleHubChange}
                  disabled={hubsLoading}
                  loading={hubsLoading}
                  placeholder={
                    hubsLoading ? 'Memuat hub…' : 'Cari atau pilih Dash Hub…'
                  }
                  emptyMessage="Hub tidak ditemukan"
                />
              </Field>

              <div className="flex items-start gap-2 rounded-lg bg-bg-weak-50 px-3 py-2.5 text-[12px] text-text-sub-600">
                <i className="ri-information-line mt-px shrink-0 text-primary" />
                <span>
                  Pengirim &amp; alamat asal terisi otomatis dari hub — bisa
                  Anda edit bila perlu.
                </span>
              </div>

              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <Field label="Nama pengirim">
                  <input
                    value={senderName}
                    onChange={(e) => setSenderName(e.target.value)}
                    placeholder="Nama pengirim"
                    className={inputCls}
                  />
                </Field>
                <Field label="Telepon asal">
                  <input
                    value={originPhone}
                    onChange={(e) => setOriginPhone(e.target.value)}
                    placeholder="Nomor telepon asal"
                    className={inputCls}
                  />
                </Field>
              </div>
            </div>

            {/* 2 · Destination */}
            <SectionHeading index={2} title="Lokasi Tujuan" />
            <div className="space-y-5">
              <Field label="Nama penerima" required>
                <input
                  value={receiverName}
                  onChange={(e) => setReceiverName(e.target.value)}
                  placeholder="Nama penerima"
                  className={inputCls}
                />
              </Field>
              <Field label="Alamat tujuan" required>
                <LocationPicker value={destination} onChange={setDestination} />
              </Field>
              <Field label="Telepon penerima">
                <input
                  value={destinationPhone}
                  onChange={(e) => setDestinationPhone(e.target.value)}
                  placeholder="Nomor HP penerima"
                  className={inputCls}
                />
              </Field>
            </div>

            {/* 3 · Packages */}
            <SectionHeading index={3} title="Paket" />
            <div className="space-y-5">
              {packages.map((pkg, i) => (
                <div
                  key={i}
                  className="rounded-xl border border-stroke-sub-300 bg-bg-white-0 p-4"
                >
                  <div className="mb-3 flex items-center justify-between">
                    <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-text-sub-600">
                      Paket {i + 1}
                    </span>
                    {packages.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removePackage(i)}
                        className="inline-flex items-center gap-1 text-[12px] font-semibold text-error-base hover:underline"
                      >
                        <i className="ri-delete-bin-line" /> Hapus
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <Field label="Nama paket" required>
                        <input
                          value={pkg.name}
                          onChange={(e) =>
                            updatePackage(i, { name: e.target.value })
                          }
                          placeholder="mis. KodeKlien-NamaPaket-001"
                          className={inputCls}
                        />
                      </Field>
                    </div>
                    <Field label="Tipe paket">
                      <select
                        value={pkg.type}
                        onChange={(e) =>
                          updatePackage(i, { type: e.target.value })
                        }
                        className={inputCls}
                      >
                        {PACKAGE_TYPES.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Berat (kg)">
                      <input
                        value={pkg.weightKg}
                        inputMode="decimal"
                        onChange={(e) =>
                          updatePackage(i, { weightKg: e.target.value })
                        }
                        placeholder="mis. 1.5"
                        className={inputCls}
                      />
                    </Field>
                    <div className="sm:col-span-2">
                      <Field label="Ukuran paket">
                        <div className="flex flex-wrap items-center gap-3">
                          <div className="inline-flex gap-1 rounded-lg border border-stroke-sub-300 bg-bg-weak-50 p-1">
                            {MANUAL_SHIPMENT_SIZES.map((s) => {
                              const active = pkg.size === s.value;
                              return (
                                <button
                                  key={s.value}
                                  type="button"
                                  onClick={() =>
                                    updatePackage(i, { size: s.value })
                                  }
                                  className={`h-8 w-11 rounded-md text-[13px] font-bold transition-colors ${
                                    active
                                      ? 'bg-bg-white-0 text-primary shadow-card-xs'
                                      : 'text-text-sub-600 hover:text-text-strong-950'
                                  }`}
                                >
                                  {s.value}
                                </button>
                              );
                            })}
                          </div>
                          <span className="text-[12px] text-text-soft-400">
                            Maks.{' '}
                            {
                              MANUAL_SHIPMENT_SIZES.find(
                                (s) => s.value === pkg.size
                              )?.dim
                            }{' '}
                            · {manualShipmentSizeVolume(pkg.size).toFixed(3)} m³
                          </span>
                        </div>
                      </Field>
                    </div>
                    <div className="sm:col-span-2">
                      <Field label="Catatan" optional>
                        <input
                          value={pkg.description}
                          onChange={(e) =>
                            updatePackage(i, { description: e.target.value })
                          }
                          placeholder="Catatan tambahan"
                          className={inputCls}
                        />
                      </Field>
                    </div>
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={addPackage}
                className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-stroke-sub-300 px-3.5 py-2 text-[13px] font-semibold text-primary transition-colors hover:bg-primary/5"
              >
                <i className="ri-add-line" /> Tambah paket
              </button>
            </div>

            {/* 4 · Options */}
            <SectionHeading index={4} title="Opsi Pengiriman" />
            <div className="space-y-5">
              <Field label="Tipe layanan">
                <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                  {visibleServiceTypes.map((svc) => {
                    const active = serviceType === svc.value;
                    return (
                      <button
                        key={svc.value}
                        type="button"
                        onClick={() => setServiceType(svc.value)}
                        className={`flex items-center gap-3 rounded-xl border px-3.5 py-3 text-left transition-colors ${
                          active
                            ? 'border-primary bg-primary/5'
                            : 'border-stroke-sub-300 hover:border-stroke-sub-300 hover:bg-bg-weak-50'
                        }`}
                      >
                        <span
                          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                            active
                              ? 'bg-primary/10 text-primary'
                              : 'bg-bg-weak-50 text-text-sub-600'
                          }`}
                        >
                          <i
                            className={`${SERVICE_ICONS[svc.value]} text-lg`}
                          />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-[13px] font-bold text-text-strong-950">
                            {svc.label}
                          </span>
                          <span className="block text-[11px] text-text-soft-400">
                            {svc.hint}
                          </span>
                        </span>
                        <span
                          className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
                            active ? 'border-primary' : 'border-stroke-sub-300'
                          }`}
                        >
                          {active && (
                            <span className="h-2 w-2 rounded-full bg-primary" />
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </Field>
              <Field label="Booking ID / Provider Order ID" optional>
                <input
                  value={bookingId}
                  onChange={(e) => setBookingId(e.target.value)}
                  placeholder="Kosongkan untuk auto (= Waybill)"
                  className={inputCls}
                />
              </Field>
            </div>
          </div>

          {/* RIGHT: summary + submit card */}
          <div className="w-full rounded-2xl border border-stroke-sub-300 bg-bg-white-0 p-5 shadow-card-xs lg:sticky lg:top-8 lg:w-[340px] lg:shrink-0 lg:p-6">
            <h3 className="text-base font-bold tracking-tight text-text-strong-950">
              Ringkasan
            </h3>
            <dl className="mt-4 space-y-3 text-sm">
              {[
                { k: 'Asal', v: selectedHub?.name || '—' },
                { k: 'Penerima', v: receiverName.trim() || '—' },
                { k: 'Layanan', v: serviceType },
                { k: 'Paket', v: `${namedPackages.length} item` },
                {
                  k: 'Total berat',
                  v: totalWeight ? `${totalWeight.toFixed(2)} kg` : '—',
                },
                {
                  k: 'Total volume',
                  v: totalVolume ? `${totalVolume.toFixed(3)} m³` : '—',
                },
              ].map((row) => (
                <div key={row.k} className="flex justify-between gap-3">
                  <dt className="text-text-sub-600">{row.k}</dt>
                  <dd className="break-words text-right font-semibold text-text-strong-950">
                    {row.v}
                  </dd>
                </div>
              ))}
            </dl>

            {inlineError && (
              <div className="mt-4 flex items-start gap-2 rounded-lg border border-error-base/30 bg-error-lighter px-3 py-2.5 text-[12px] text-error-base">
                <i className="ri-error-warning-line mt-px shrink-0" />
                <span>{inlineError}</span>
              </div>
            )}

            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={submitting}
              className="mt-5 w-full justify-center"
            >
              {submitting ? (
                <>
                  <i className="ri-loader-4-line mr-1.5 animate-spin" />
                  Membuat…
                </>
              ) : (
                'Buat Shipment'
              )}
            </Button>
            <Button
              variant="white"
              onClick={onClose}
              disabled={submitting}
              className="mt-2 w-full justify-center"
            >
              Batal
            </Button>
            <p className="mt-3 text-center text-[11px] leading-relaxed text-text-soft-400">
              Shipment dibuat lewat pipeline import — alamat &amp; zona
              diselesaikan otomatis.
            </p>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CreateShipmentModal;
